import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useCallback, useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useUpdateMutation } from "../../../api/genericHooks";
import { BookContext } from "../../../contexts/BookContext";
import { Step } from "../GenerateBookPDFModal";
import { GenPDFStepper } from "./GenPDFStepper";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  steps: Step[];
  nextStep: () => void;
};

export type GenerateBookPDFData = {
  summary: string;
};

export const GenerateBookPDFModalStep4: FC<Props> = ({
  open,
  setOpen,
  steps,
  nextStep,
}) => {
  const { t } = useTranslation();
  const { book, refresh } = useContext(BookContext);

  // Form Methods =============================================================
  const GeneratePDF2FormSchema = Yup.object().shape({
    summary: Yup.string().required(t("errors.required")),
  });

  const defaultValues = useMemo<GenerateBookPDFData>(() => {
    return {
      summary: book?.summary || "",
    };
  }, [book]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<GenerateBookPDFData>({
    defaultValues: defaultValues,
    resolver: yupResolver(GeneratePDF2FormSchema),
  });

  const summary = watch("summary");

  // Mutations ===============================================================
  const updateBookMutation = useUpdateMutation({
    route: "/books/:id",
    pathParams: { id: book?.id },
  });

  // Form Submit =============================================================
  const onSubmit = useCallback(
    (values: GenerateBookPDFData) => {
      updateBookMutation
        .mutateAsync({
          book: { summary: values.summary },
        })
        .then(() => {
          toast.success(t("generateBookPDFModal.saveSuccess"));
          refresh();
          nextStep();
        });
    },
    [refresh, t]
  );

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <form
              className="bg-white flex flex-1 flex-col justify-between"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon aria-hidden="true" className="size-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <DialogTitle
                    as="h3"
                    className="text-2xl font-semibold text-slate-800"
                  >
                    {t("generateBookPDFModal.title")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />

                  <GenPDFStepper steps={steps} />
                  <div className="flex flex-row mt-5">
                    <div className="w-full">
                      <div
                        className="mt-2 text-sm text-slate-700"
                        dangerouslySetInnerHTML={{
                          __html: t("generateBookPDFModal.summaryDescription", {
                            interpolation: {
                              escapeValue: false,
                            },
                          }),
                        }}
                      />
                      <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="sm:col-span-6">
                          <label
                            htmlFor="author_birthdate"
                            className="block text-sm/6 font-medium text-slate-900"
                          >
                            {t("generateBookPDFModal.summary")}
                          </label>
                          <div className="mt-2">
                            <textarea
                              id="summary"
                              rows={13}
                              {...register("summary", { required: true })}
                              autoComplete="summary"
                              maxLength={1300}
                              className="block w-full rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-delicate-600 sm:text-sm/6"
                            />
                            <div className="text-right text-sm/6 text-slate-400">
                              {summary.length} / 1300
                            </div>
                          </div>
                          {errors.summary && (
                            <span className="text-rust-400 font-nunito">
                              {errors.summary.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  className="mt-3 inline-flex w-full justify-center rounded bg-rust-50 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:mt-0 sm:w-auto"
                >
                  {t("addPicturesModal.save")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
